import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { ValidationStatus } from './_anonymous.sign-up/ValidationStatus';
import allocabLogo from '../assets/images/allocab-logo.svg';
import { CheckBox } from '../components/atomic/CheckBox';
import { Link } from '../components/atomic/Link';
import { TextInput } from '../components/atomic/TextInput';
import { PrimaryButton } from '../components/button/PrimaryButton';
import { PhoneInputControlled } from '../components/form/PhoneInputControlled';
import { useAuth } from '../hooks/useAuth';
import { ToastManager } from '../services/toast/utils';
import { t } from '../services/translation/translation';
import { rules } from '../utils/validation';

type FieldValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordConfirm: string;
  phoneNumber: string;
  phoneNumberCountry: string;
};

export const SignUpRoute = () => {
  const [isChecked, setIsChecked] = useState(false);
  const { signupMutation } = useAuth();
  const { state } = useLocation();
  const methods = useForm<FieldValues>({ defaultValues: { password: '', email: state?.initialEmail } });
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = methods;

  const password = watch('password');
  const email = watch('email');

  const onSubmit = handleSubmit(({ firstName, lastName, email, password, phoneNumber, phoneNumberCountry }) => {
    if (!isChecked) {
      ToastManager.show({ message: t('sign-up-route.should-accept'), severity: 'error' });
    } else {
      signupMutation.mutate({
        gender: 'MALE',
        firstName,
        lastName,
        email,
        password,
        phoneNumber,
        phoneNumberCountry,
      });
    }
  });

  return (
    <section className="bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto min-h-screen">
        <div className="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white">
          <img className="h-10 w-auto" src={allocabLogo} alt="allocab" />
        </div>
        <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-lg xl:p-0 dark:bg-gray-800 dark:border-gray-700">
          <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
            <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
              {t('sign-up-route.title')}
            </h1>
            <form className="space-y-4" onSubmit={onSubmit}>
              <FormProvider {...methods}>
                <div className="grid grid-cols-2 gap-4">
                  <TextInput
                    type="firstName"
                    id="firstName"
                    placeholder="Jean"
                    label={t('common.first-name')}
                    errorMessage={errors.firstName?.message}
                    {...register('firstName', { required: rules.required })}
                  />
                  <TextInput
                    type="lastName"
                    id="lastName"
                    placeholder="Dupont"
                    label={t('common.last-name')}
                    errorMessage={errors.lastName?.message}
                    {...register('lastName', { required: rules.required })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <TextInput
                    type="email"
                    id="email"
                    placeholder="jean@dupont.com"
                    label={t('common.email')}
                    errorMessage={errors.email?.message}
                    {...register('email', { required: rules.required, validate: rules.email })}
                  />
                  <PhoneInputControlled
                    name="phoneNumber"
                    countryName="phoneNumberCountry"
                    label={t('common.phone')}
                    rules={{
                      required: rules.required,
                      validate: rules.phone,
                    }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <TextInput
                    type="password"
                    id="password"
                    placeholder="••••••••"
                    label={t('common.password')}
                    errorMessage={errors.password?.message}
                    {...register('password', { required: rules.required, pattern: rules.password })}
                  />
                  <TextInput
                    type="password"
                    id="passwordConfirm"
                    placeholder="••••••••"
                    label={t('common.password-confirm')}
                    errorMessage={errors.passwordConfirm?.message}
                    {...register('passwordConfirm', {
                      required: rules.required,
                      validate: (value, fields) => (value === fields.password ? undefined : t('form.errors.no-match')),
                    })}
                  />
                </div>

                <ValidationStatus value={password} />

                <CheckBox
                  id="cgp"
                  checked={isChecked}
                  onChange={setIsChecked}
                  className="hover:bg-inherit dark:hover:bg-inherit py-0"
                  label={
                    <>
                      {t('sign-up-route.accept-cgp')}
                      <Link to="https://www.allocab.com/conditions-generales-partenariat-driver/" target="_blank">
                        {t('common.cgp')}
                      </Link>
                    </>
                  }
                />
                <PrimaryButton
                  type="submit"
                  className="w-full"
                  text={t('sign-up-route.cta')}
                  isLoading={signupMutation.isLoading}
                />
              </FormProvider>
            </form>
            <p className="text-sm font-light text-gray-500 dark:text-gray-400">
              {t('sign-up-route.already-account')}
              <Link
                to="/login"
                state={{ initialEmail: email }}
                className="font-medium text-blue-600 hover:underline dark:text-blue-500 ml-1"
              >
                {t('sign-up-route.login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
